import { Input } from "@egaranti/components";
import { Popover, PopoverContent, PopoverTrigger } from "@egaranti/components";
import { Button } from "@egaranti/components";

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import useIndividualCustomerStore from "@/stores/useIndividualCustomerStore";

import { Search, SlidersHorizontal, X } from "lucide-react";

const IndividualCustomerFilterBar = () => {
  const { t } = useTranslation();
  const { filters, getIndividuaCustomers, setFilters, clearAllFilters } =
    useIndividualCustomerStore();

  // Local search state
  const [searchValue, setSearchValue] = useState(filters.query || "");

  // Advanced filters popover
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Local advanced filter states
  const [localFirstName, setLocalFirstName] = useState(filters.firstName || "");
  const [localLastName, setLocalLastName] = useState(filters.lastName || "");
  const [localEmail, setLocalEmail] = useState(filters.email || "");
  const [localPhone, setLocalPhone] = useState(filters.phone || "");
  const [localDateStart, setLocalDateStart] = useState(
    filters.createdAtStart || "",
  );
  const [localDateEnd, setLocalDateEnd] = useState(filters.createdAtEnd || "");

  // Sync local states with filters
  useEffect(() => {
    setSearchValue(filters.query || "");
    setLocalFirstName(filters.firstName || "");
    setLocalLastName(filters.lastName || "");
    setLocalEmail(filters.email || "");
    setLocalPhone(filters.phone || "");
    setLocalDateStart(filters.createdAtStart || "");
    setLocalDateEnd(filters.createdAtEnd || "");
  }, [filters]);

  // Handlers
  const handleSearch = useCallback(() => {
    setFilters({ ...filters, query: searchValue, page: 1 });
    getIndividuaCustomers();
  }, [searchValue, filters, setFilters, getIndividuaCustomers]);

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const clearSearch = () => {
    setSearchValue("");
    setFilters({ ...filters, query: "", page: 1 });
    getIndividuaCustomers();
  };

  // Apply advanced filters
  const applyAdvancedFilters = () => {
    setFilters({
      ...filters,
      firstName: localFirstName,
      lastName: localLastName,
      email: localEmail,
      phone: localPhone,
      createdAtStart: localDateStart,
      createdAtEnd: localDateEnd,
      page: 1,
    });
    getIndividuaCustomers();
    setAdvancedOpen(false);
  };

  // Clear advanced filters
  const clearAdvancedFilters = () => {
    setLocalFirstName("");
    setLocalLastName("");
    setLocalEmail("");
    setLocalPhone("");
    setLocalDateStart("");
    setLocalDateEnd("");
    setFilters({
      ...filters,
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      createdAtStart: "",
      createdAtEnd: "",
      page: 1,
    });
    getIndividuaCustomers();
    setAdvancedOpen(false);
  };

  // Check if any filters are active
  const hasActiveFilters =
    filters.query ||
    filters.firstName ||
    filters.lastName ||
    filters.email ||
    filters.phone ||
    filters.createdAtStart ||
    filters.createdAtEnd;

  // Count advanced filters
  const advancedFilterCount = [
    filters.firstName,
    filters.lastName,
    filters.email,
    filters.phone,
    filters.createdAtStart || filters.createdAtEnd,
  ].filter(Boolean).length;

  return (
    <div className="w-full rounded-t-lg border-b bg-white">
      {/* Main Filter Row */}
      <div className="flex flex-wrap items-center gap-3 px-6 py-4">
        {/* Search Input */}
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9AA4B2]" />
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder={t("clientsPage.searchCustomers", "Müşteri ara...")}
            className="h-10 w-full rounded-lg border border-[#E3E8EF] bg-white pl-10 pr-9 text-sm text-[#111729] placeholder-[#9AA4B2] transition-colors focus:border-[#0049E6] focus:outline-none focus:ring-1 focus:ring-[#0049E6]"
          />
          {searchValue && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9AA4B2] hover:text-[#364152]"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Divider */}
        <div className="h-8 w-px bg-[#E3E8EF]" />

        {/* Advanced Filters Popover */}
        <Popover open={advancedOpen} onOpenChange={setAdvancedOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="secondaryGray"
              className={`h-10 gap-2 border-[#E3E8EF] ${advancedFilterCount > 0 ? "border-[#0049E6] bg-[#EEF4FF] text-[#0049E6]" : ""}`}
            >
              <SlidersHorizontal className="h-4 w-4" />
              {t("productsPage.advancedFilters", "Gelişmiş")}
              {advancedFilterCount > 0 && (
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#0049E6] text-xs text-white">
                  {advancedFilterCount}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full bg-white p-4" align="start">
            <div className="space-y-4">
              <h4 className="font-medium text-[#111729]">
                {t("productsPage.advancedFilters", "Gelişmiş Filtreler")}
              </h4>

              {/* First Name */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#364152]">
                  {t("common.firstName", "Ad")}
                </label>
                <Input
                  type="text"
                  placeholder={t("common.firstName", "Ad")}
                  value={localFirstName}
                  onChange={(e) => setLocalFirstName(e.target.value)}
                  className="h-9"
                />
              </div>

              {/* Last Name */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#364152]">
                  {t("common.lastName", "Soyad")}
                </label>
                <Input
                  type="text"
                  placeholder={t("common.lastName", "Soyad")}
                  value={localLastName}
                  onChange={(e) => setLocalLastName(e.target.value)}
                  className="h-9"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#364152]">
                  {t("common.email", "E-posta")}
                </label>
                <Input
                  type="email"
                  placeholder={t("common.email", "E-posta")}
                  value={localEmail}
                  onChange={(e) => setLocalEmail(e.target.value)}
                  className="h-9"
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#364152]">
                  {t("common.phone", "Telefon")}
                </label>
                <Input
                  type="text"
                  placeholder={t("common.phone", "Telefon")}
                  value={localPhone}
                  onChange={(e) => setLocalPhone(e.target.value)}
                  className="h-9"
                />
              </div>

              {/* Date Range */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#364152]">
                  {t("productsPage.dateRange", "Kayıt Tarihi Aralığı")}
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={localDateStart}
                    onChange={(e) => setLocalDateStart(e.target.value)}
                    className="h-9 flex-1"
                  />
                  <span className="text-[#9AA4B2]">-</span>
                  <Input
                    type="date"
                    value={localDateEnd}
                    onChange={(e) => setLocalDateEnd(e.target.value)}
                    className="h-9 flex-1"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAdvancedFilters}
                  className="text-[#677389]"
                >
                  {t("common.clear", "Temizle")}
                </Button>
                <Button size="sm" onClick={applyAdvancedFilters}>
                  {t("common.apply", "Uygula")}
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* Clear All - Right aligned */}
        {hasActiveFilters && (
          <button
            onClick={clearAllFilters}
            className="ml-auto flex items-center gap-1 text-sm font-medium text-[#677389] hover:text-[#364152]"
          >
            <X className="h-4 w-4" />
            {t("common.clearAll", "Temizle")}
          </button>
        )}
      </div>
    </div>
  );
};

export default IndividualCustomerFilterBar;
